/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'

import ChevronDownIcon from '@offerzen/zenicon/ChevronDownIcon'
import { colors } from '../../../themes'
import FormErrorMessage from '../../FormErrorMessage'
import FormHelpText from '../../FormHelpText'
import AccessibleIcon from '../../AccessibleIcon'

import styles, { wrapperTheme, labelTheme } from './styles'

class SingleSelectField extends Component {
  constructor(props) {
    super(props)

    const value = this.props.input.value || this.props.value
    const selectedOptionIndex = React.Children.toArray(this.props.children).findIndex(child => child.props.value === value)

    this.state = {
      open: false,
      focusIndex: -1,
      selectedOptionId: null,
      selectedOptionIndex,
    }

    this.onBlurTimeout = null
  }

  componentDidUpdate(_, prevState) {
    if (prevState.open !== this.state.open) {
      if (this.state.open) {
        this.focusOnSelectedChild()
        this.body.focus()
      } else {
        this.input.focus()
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.onBlurTimeout)
  }

  onLabelClick = ev => {
    const { open } = this.state

    this.setState({ open: !open })

    const { input, ...props } = this.props

    const focusHandler = input.onFocus || props.onFocus
    if (!open && focusHandler) focusHandler(ev)

    const blurHandler = input.onBlur || props.onBlur
    if (open && blurHandler) blurHandler(ev)
  }

  onChildClick = (childValue, childIndex, childId) => {
    const { input, ...props } = this.props
    const name = input.name || props.name
    const value = input.value || props.value

    this.setState({
      open: false,
      focusIndex: childIndex,
      selectedOptionIndex: childIndex,
      selectedOptionId: childId,
    })

    // Call the handlers
    const handler = input.onChange || props.onChange
    if (handler) handler(childValue)
    if (props.onSelect) props.onSelect(name, childValue, value)
  }

  onOverlayClick = ev => {
    this.setState({ open: false })

    const { input, ...props } = this.props
    const handler = input.onBlur || props.onBlur
    if (handler) handler(ev)
  }

  focusOnSelectedChild = () => {
    const { selectedOptionIndex } = this.state
    if (selectedOptionIndex < 0) return

    // If the body is scrollable, make sure the selected option is visible
    // Also, we're making sure that the reference exists to avoid null errors
    let selectedOption
    if (this.body && this.body.scrollHeight > this.body.offsetHeight) {
      selectedOption = this.body.children[selectedOptionIndex]
      this.body.scrollTop = selectedOption.offsetTop - this.body.offsetHeight * 0.5
    }

    this.setState({
      selectedOptionId: selectedOption && selectedOption.id,
      focusIndex: selectedOptionIndex,
    })
  }

  handleButtonNavigation = ev => {
    const focusIndex = this.keyboardFocusIndex()

    switch (ev.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
      case 'Spacebar': // 'Spacebar' is support for IE and older browsers
      case 'Enter':
        ev.preventDefault()

        if (!this.state.open) this.setState({ open: true, focusIndex })

        break

      case 'Escape':
      case 'Esc': // 'Esc' is support for IE and older browsers
        ev.preventDefault()

        this.setState({ open: false, focusIndex: -1 })

        break

      default:
        break
    }
  }

  handleOptionsNavigation = ev => {
    const focusIndex = this.keyboardFocusIndex()
    switch (ev.key) {
      case 'ArrowUp':
      case 'ArrowDown': {
        ev.preventDefault()

        const offsetDirection = ev.key === 'ArrowDown' ? 1 : -1
        const focusOffset = focusIndex + offsetDirection
        if (focusOffset >= 0 && focusOffset < React.Children.count(this.props.children)) {
          this.setState({ focusIndex: focusOffset })

          // If the body is scrollable, make sure the selected option is visible
          // Also, we're making sure that the reference exists to avoid null errors
          if (this.body && this.body.scrollHeight > this.body.offsetHeight) {
            // Get the reference of the new selected option
            const selectedOption = this.body.children[focusOffset]
            // Calculate the visible area of the menu
            const visibleArea = this.body.scrollTop + this.body.offsetHeight

            this.setState({ selectedOptionId: selectedOption.id })

            if (selectedOption.offsetTop < this.body.scrollTop) {
              selectedOption.scrollIntoView()
            } else if (selectedOption.offsetTop + selectedOption.offsetHeight > visibleArea) {
              // eslint-disable-next-line operator-assignment
              this.body.scrollTop = this.body.scrollTop + selectedOption.offsetHeight
            }
          }
        }

        break
      }

      case 'Escape':
      case 'Esc': {
        // 'Esc' is support for IE and older browsers
        ev.preventDefault()

        this.setState({ open: false, focusIndex: -1 })

        break
      }

      case 'Enter': {
        ev.preventDefault()

        const selectedChild = React.Children.toArray(this.props.children)[focusIndex]
        const childValue = selectedChild.props.value
        const childId = this.constructChildId(childValue)
        this.onChildClick(childValue, focusIndex, childId)

        if (this.input) this.input.blur()

        break
      }

      default:
        break
    }
  }

  handleBlur = ev => {
    ev.persist()

    // https://reactjs.org/docs/accessibility.html#mouse-and-pointer-events
    //
    // We close the popover on the next tick by using setTimeout.
    // This is necessary because we need to first check if an
    // option has been selected as the blur event fires prior
    // to the new focus event.
    this.onBlurTimeout = setTimeout(() => {
      const handler = this.props.input.onBlur || this.props.onBlur
      if (handler) handler()

      this.setState({ open: false, focusIndex: -1 })
    })
  }

  onOptionsFocus = () => {
    // Cancels the timeout, preventing
    // onBlur handlers from being called
    clearTimeout(this.onBlurTimeout)
  }

  constructChildId = childValue => `${this.props.id}-option-${childValue}`

  // When opening with keyboard, the first item should be
  // highlighted if nothing has been selected yet
  keyboardFocusIndex = () => (this.state.focusIndex < 0 ? 0 : this.state.focusIndex)

  getActiveDescendent = () => {
    const children = React.Children.toArray(this.props.children)

    if (!children.length) return

    // eslint-disable-next-line consistent-return
    return this.state.selectedOptionId || this.constructChildId(children[0].props.value)
  }

  render() {
    const { open, focusIndex } = this.state
    const { meta, input, label, theme, helpText, readOnly, id, ...props } = this.props

    const children = React.Children.toArray(props.children)
    const value = input.value || props.value
    const errored = meta.touched && meta.error

    const selectedChild = children.find(child => child.props.value === value) || {
      props: { value: '', label: props.placeholder, disabled: true },
    }
    const isPlaceholder = selectedChild.props.value === '' && selectedChild.props.disabled

    const activeDescendant = this.getActiveDescendent()

    const listboxId = `${id}-listbox`

    return (
      <div className={cx(styles.wrapper, props.className, wrapperTheme(theme), errored && styles.wrapperError)}>
        {/* Render body first so the "input" appears on top */}
        {Boolean(open) && (
          <>
            <ul
              id={listboxId}
              className={styles.body}
              role="listbox"
              aria-activedescendant={activeDescendant}
              tabIndex="-1"
              // eslint-disable-next-line no-return-assign
              ref={el => (this.body = el)}
              onKeyDown={this.handleOptionsNavigation}
              onFocus={this.onOptionsFocus}
              // Isn't read out, but is required for popup listbox to focus correctly
              aria-label="Select an option">
              {React.Children.map(children, (child, index) => {
                const childValue = child.props.value
                const childId = this.constructChildId(childValue)

                return React.cloneElement(child, {
                  theme,
                  onClick: () => this.onChildClick(childValue, index, childId),
                  selected: value === childValue,
                  focused: focusIndex === index ? 'true' : null,
                  id: childId,
                  onMouseEnter: () => this.setState({ focusIndex: index }),
                })
              })}
            </ul>
            <div data-testid="hack-overlay" className={styles.overlay} onClick={this.onOverlayClick} />
          </>
        )}
        <div
          className={cx(styles.label, labelTheme(theme), open && styles.openLabel, errored && styles.errorLabel)}
          readOnly={readOnly}
          onClick={this.onLabelClick}
          tabIndex={readOnly ? '-1' : props.tabIndex}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${props.ariaLabelledby || id || ''}`}
          aria-owns={listboxId}
          onKeyDown={this.handleButtonNavigation}
          onBlur={this.handleBlur}
          ref={el => {
            this.input = el
            // eslint-disable-next-line no-unused-expressions
            this.props.setInnerRef && this.props.setInnerRef(el)
          }}>
          <div id={id} className={cx(styles.input, isPlaceholder && styles.placeholderInput)} data-test={props.dataTest}>
            {Boolean(label) && <>{label}:&nbsp;</>}
            {selectedChild.props.label}
          </div>

          <div className={styles.arrow}>
            <AccessibleIcon icon={ChevronDownIcon} size={18} color={errored ? colors.mandy : colors.geyser} />
          </div>
        </div>
        {Boolean(helpText && !errored) && <FormHelpText text={helpText} />}
        {Boolean(errored) && <FormErrorMessage text={meta.error} />}
      </div>
    )
  }
}

SingleSelectField.propTypes = {
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  name: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  dataTest: PropTypes.string,
  onSelect: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  helpText: PropTypes.string,
  tabIndex: PropTypes.string,
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'dark', 'tertiary', 'info', 'light']),
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  id: PropTypes.string.isRequired,
  setInnerRef: PropTypes.func,
}

SingleSelectField.defaultProps = {
  meta: {},
  input: {},
  value: '',
  placeholder: '',
  theme: 'primary',
  tabIndex: '0',
}

export default SingleSelectField
